import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { get, isEqual } from 'lodash-es';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { fireQuoteRecencyTrackingPixel } from 'site-modules/shared/utils/appraisal/common-utils';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { Storage } from 'site-modules/shared/utils/storage';
import {
  ADDRESS_TAB_ID,
  VIN_TAB_ID,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tab-constants';
import { APPRAISAL_STORAGE_KEYS } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/license-plate-and-vin-step/constants';
import { APPRAISAL_VEHICLE_ENTRY_VIN_LOOKUP_CREATIVE_ID } from 'client/site-modules/shared/constants/appraisal/appraisal';

const VIN_LOOKUP_BTN_CTA = 'VIN Lookup';

/**
 * Custom hook for managing tab state and interactions.
 * Extracted from the AppraisalTabsUI class component.
 *
 * State for activeTabId, tabs, and prepopulatedVehicle lives in context (single source of truth).
 * This hook reads from context and calls context setters directly.
 */
export function useTabManagement({
  initialTabs,
  initialActiveTabId,
  activeTabId,
  tabs,
  storageFromProps,
  creativeId,
  isLookUpStyleView,
  isVisibleInDrawer,
  isProfileOfferVinPrepopulationEnabled,
  profileOfferVin,
  vinModsData,
  setActiveTabId,
  setTabs,
  setPrepopulatedVehicle,
}) {
  const [isPrivatePartyBtn, setIsPrivatePartyBtn] = useState(false);

  const storage = useMemo(() => storageFromProps || new Storage('sessionStorage'), [storageFromProps]);

  // Track previous values for componentDidUpdate-equivalent effects
  const prevIsVisibleInDrawerRef = useRef(isVisibleInDrawer);
  const prevProfileOfferVinRef = useRef(profileOfferVin);
  const prevVinModsDataRef = useRef(vinModsData);

  const fireTabTracking = useCallback(
    (tabsList, tabId) => {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: TrackingConstant.TAB_DEFAULT,
          creative_id: creativeId,
          value: tabsList.find(({ id }) => id === tabId)?.label,
        },
      });
    },
    [creativeId]
  );

  const setActiveTab = useCallback(
    tabId =>
      delayHeavyTask(() => {
        setActiveTabId(tabId);
        storage.set(APPRAISAL_STORAGE_KEYS.APPRAISAL_TABS_ACTIVE_TAB, tabId);
      }),
    [setActiveTabId, storage]
  );

  const handleTabLinkClick = useCallback(
    event => {
      const tabId = event.currentTarget.dataset.tabId;
      setActiveTab(tabId);
    },
    [setActiveTab]
  );

  const handlePrepopulatedVehicle = useCallback(
    vehicle => {
      setPrepopulatedVehicle(vehicle);
    },
    [setPrepopulatedVehicle]
  );

  const handleBtnViewChange = useCallback(value => {
    setIsPrivatePartyBtn(value);
  }, []);

  const handleVinLookupBtnClick = useCallback(() => {
    setActiveTab(ADDRESS_TAB_ID);
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_START,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_name: TrackingConstant.ACTION_TOOL_SELECT,
        subaction_name: TrackingConstant.TAB_SELECT,
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        creative_id: APPRAISAL_VEHICLE_ENTRY_VIN_LOOKUP_CREATIVE_ID,
        value: VIN_LOOKUP_BTN_CTA,
      },
    });
  }, [setActiveTab]);

  // componentDidMount equivalent: initialize context state and fire tracking
  useEffect(() => {
    if (!tabs.length && !activeTabId) {
      setActiveTabId(initialActiveTabId);
      setTabs(initialTabs);
    }

    if (!isLookUpStyleView) {
      fireTabTracking(initialTabs, initialActiveTabId);
    }

    // Inline handler avoids stale-closure issues with useCallback identity vs
    // the one-time addEventListener registration.
    const clearStorage = () => {
      if (!storageFromProps) {
        storage.remove(APPRAISAL_STORAGE_KEYS.LICENSE_PLATE);
        storage.remove(APPRAISAL_STORAGE_KEYS.STATE_CODE);
        storage.remove(APPRAISAL_STORAGE_KEYS.VIN);
        storage.remove(APPRAISAL_STORAGE_KEYS.APPRAISAL_TABS_ACTIVE_TAB);
      }
    };

    window.addEventListener('beforeunload', clearStorage);

    if (storageFromProps) {
      const savedActiveTabId = storageFromProps.get(APPRAISAL_STORAGE_KEYS.APPRAISAL_TABS_ACTIVE_TAB);
      if (savedActiveTabId) {
        setActiveTab(savedActiveTabId);
      }
    }

    return () => {
      window.removeEventListener('beforeunload', clearStorage);
    };
  }, []);

  // componentDidUpdate: isVisibleInDrawer tracking
  useEffect(() => {
    if (isVisibleInDrawer && !isEqual(prevIsVisibleInDrawerRef.current, isVisibleInDrawer)) {
      fireTabTracking(tabs, activeTabId);
    }
    prevIsVisibleInDrawerRef.current = isVisibleInDrawer;
  }, [isVisibleInDrawer, tabs, activeTabId, fireTabTracking]);

  // componentDidUpdate: profileOfferVin → switch to VIN tab
  useEffect(() => {
    if (
      isProfileOfferVinPrepopulationEnabled &&
      prevProfileOfferVinRef.current !== profileOfferVin &&
      profileOfferVin
    ) {
      setActiveTab(VIN_TAB_ID);
    }
    prevProfileOfferVinRef.current = profileOfferVin;
  }, [isProfileOfferVinPrepopulationEnabled, profileOfferVin, setActiveTab]);

  // componentDidUpdate: vinModsData → fire quote recency tracking
  useEffect(() => {
    if (!isEqual(vinModsData, prevVinModsDataRef.current)) {
      fireQuoteRecencyTrackingPixel(get(vinModsData, 'kmx.offersFinalizedAt'));
    }
    prevVinModsDataRef.current = vinModsData;
  }, [vinModsData]);

  return {
    isPrivatePartyBtn,
    storage,
    setActiveTab,
    handleTabLinkClick,
    handlePrepopulatedVehicle,
    handleBtnViewChange,
    handleVinLookupBtnClick,
  };
}
